/**
 * ConfirmDialog — reusable "are you sure?" modal (ported from sk-catalog so the
 * voice suite shares one confirm pattern). Destructive default (danger accent).
 * Uses the billing theme + SkIcon.
 */
import React from 'react';
import { Modal, View, Text, Pressable, StyleSheet, Dimensions, Platform, TouchableOpacity } from 'react-native';
import SkIcon from './skIcon';
import { useTheme } from '../context/themeContext';

const { width } = Dimensions.get('window');

type ConfirmDialogProps = Readonly<{
  visible: boolean;
  title?: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: string;
  busy?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
}>;

export default function ConfirmDialog({
  visible,
  title = 'Confirm action',
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor,
  busy = false,
  onClose,
  children,
}: ConfirmDialogProps) {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const accentColor = confirmColor ?? colors.danger;

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View style={[styles.box, { backgroundColor: colors.card }]}>
          <View style={[styles.indicator, { backgroundColor: accentColor }]} />

          {onClose && (
            <TouchableOpacity
              style={[styles.closeButton, { backgroundColor: colors.paper2 }]}
              onPress={onClose}
              hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
            >
              <SkIcon name="x" size={20} color={colors.ink4} />
            </TouchableOpacity>
          )}

          <View style={styles.content}>
            <Text style={[styles.title, { color: colors.ink, fontFamily: font.sansBold }]}>{title}</Text>
            <Text style={[styles.message, { color: colors.ink3, fontFamily: font.sans }]}>{message}</Text>
            {children}
          </View>

          <View style={[styles.footer, { backgroundColor: colors.paper2 }]}>
            <Pressable
              style={({ pressed }) => [styles.btn, { backgroundColor: colors.paper3 }, pressed && styles.btnPressed]}
              onPress={onCancel}
              disabled={busy}
            >
              <Text style={[styles.btnText, { color: colors.ink2, fontFamily: font.sansSemibold }]}>{cancelText}</Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [styles.btn, { backgroundColor: accentColor, opacity: busy ? 0.7 : 1 }, pressed && styles.btnPressed]}
              onPress={onConfirm}
              disabled={busy}
            >
              <Text style={[styles.btnText, { color: '#fff', fontFamily: font.sansSemibold }]}>{busy ? 'Working…' : confirmText}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.55)', justifyContent: 'center', alignItems: 'center', paddingHorizontal: 24 },
  box: {
    width: Math.min(width * 0.8, 340), borderRadius: 16, overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 16px 48px rgba(16,24,40,.25)' } as any) : { elevation: 10 }),
  },
  indicator: { height: 4, width: '100%' },
  content: { paddingTop: 20, paddingHorizontal: 18, paddingBottom: 16, alignItems: 'center' },
  title: { fontSize: 16, fontWeight: '700', textAlign: 'center', marginBottom: 8, letterSpacing: -0.2 },
  message: { fontSize: 13, textAlign: 'center', lineHeight: 19 },
  footer: { flexDirection: 'row', padding: 14, gap: 10 },
  btn: { flex: 1, height: 42, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  btnPressed: { transform: [{ translateY: 0.5 }], opacity: 0.85 },
  btnText: { fontSize: 14, fontWeight: '600' },
  closeButton: { position: 'absolute', top: 14, right: 14, zIndex: 10, borderRadius: 16, width: 30, height: 30, justifyContent: 'center', alignItems: 'center' },
});
